import React, {
  createContext,
  FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback
} from 'react';
import { useClient, useOnboardingLocalStorage } from '@/hooks';
import {
  EmptyObject,
  ErrorI,
  GetCategoriesI,
  GetVendorInfoI,
  PostVendorInfoI,
  UserWithVendorI,
  VerifyUsernameI
} from '@/types/cyclone/requests';
import { toast } from 'react-toastify';
import { RefetchOptions, useMutation, UseMutationResult, useQuery } from 'react-query';
import { CategoryI, SubCategoryI } from '@/types/cyclone/models';
import { trackGenericEvent } from '@/analytics';
import { Control, DeepMap, FieldError, FieldValues, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { getSchema, MIN_USERNAME_LENGTH, SPECIAL_CHARS_REGEX, TILDE_REGEX } from '../utils';
import { updateIntercomPhone } from '@/services/intercom';
import { Loader } from '@/components/Spinner/Loader';

type OnboardingContextType = {
  user: UserWithVendorI | undefined;
  mutation?: UseMutationResult<Partial<PostVendorInfoI>, ErrorI, void, unknown>;
  refetch: (options?: RefetchOptions | undefined) => void;
  step: number;
  setStep: (step: number) => void;
  setValue: (name: string, value: any) => void;
  loadingState: boolean;
  setLoadingState: (loadingState: boolean) => void;
  //LOCATION----------------------------------------
  country: string | null;
  setCountry: (country: string) => void;
  state: string | null;
  setState: (state: string) => void;
  timezone: string | null;
  setTimeZone: (timezone: string) => void;
  onSubmitLocation: () => void;
  //CATEGORIES-------------------------------------
  categories: CategoryI[] | undefined;
  handleFatherCategory: (category: CategoryI) => void;
  fatherCategory: CategoryI | undefined;
  handleChildCategory: (subCategory: SubCategoryI) => void;
  childCategories: SubCategoryI[];
  onSubmitChildCategory: () => void;
  //SITE DATA--------------------------------------
  onSubmitSiteData: (data: FieldValues) => void;
  username: string;
  control: Control<FieldValues>;
  usernameIsAvailable: boolean;
  fullSubmitSiteData: () => void;
  errors: DeepMap<FieldValues, FieldError>;
  togglePrivacyPolicies: () => void;
  privacyPolicies: boolean;
  hasValidationError: boolean;
  validationChecks: Record<string, boolean>;
  //PHONE------------------------------------------
  phone: string;
  onChangePhone: (phone: string) => void;
  onSubmitPhone: () => void;
};

const OnboardingContext = createContext<OnboardingContextType>({} as OnboardingContextType);

interface OnBoardingProviderPropsI {
  children?: React.ReactNode;
}

export const OnboardingProvider: FunctionComponent<OnBoardingProviderPropsI> = ({ children }) => {
  const { client } = useClient();
  const {
    state: localState,
    updateState: updateLocalState,
    markStepCompleted,
    hasStoredData
  } = useOnboardingLocalStorage();

  // Use localStorage state as primary source, with fallback to URL params
  const [step, setStepState] = useState<number>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get('step');

    if (hasStoredData && localState.step > 1) {
      return localState.step;
    }
    return stepParam ? parseInt(stepParam) : 1;
  });

  const [justSavedSiteData, setJustSavedSiteData] = useState(false);

  const queryParams = useMemo(() => {
    return new URLSearchParams(window.location.search);
  }, []);

  // Memoized step management with localStorage integration
  const setStepWithPersistence = useCallback(
    (newStep: number) => {
      setStepState(newStep);
      updateLocalState({ step: newStep });

      // Update URL for bookmarking
      if (window.location.pathname.includes('wizard')) {
        queryParams.set('step', newStep.toString());
        window.history.replaceState({}, '', `${window.location.pathname}?${queryParams}`);
      }
    },
    [updateLocalState, queryParams]
  );

  // Optimized user data query with better error handling
  const { data: user, refetch } = useQuery(
    ['me', 'info'],
    async () =>
      await client<GetVendorInfoI>(`me/vendor/info`, 'GET', {
        isAuthRequired: true
      }),
    {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onError: () => {
        toast.error('Error al cargar los datos del usuario');
      }
    }
  );

  // Auto-redirect to correct step based on username completion
  useEffect(() => {
    if (
      user &&
      step === 6 &&
      (typeof user?.vendor?.username === 'undefined' ||
        user.vendor.username === null ||
        user.vendor.username === '') &&
      !justSavedSiteData
    ) {
      setTimeout(() => {
        setStepWithPersistence(5);
      }, 500);
    }
  }, [user, step, setStepWithPersistence, justSavedSiteData]);

  // Optimized mutation with better error handling
  const mutation = useMutation<Partial<PostVendorInfoI>, ErrorI>(
    (vendor) =>
      client(`me/vendor/info`, 'PUT', {
        isAuthRequired: true,
        data: vendor
      }),
    {
      onSuccess: () => {
        refetch();
        const nextStep = step === 1 && fatherCategory?.name !== 'Belleza y Estética' ? 3 : step + 1;
        markStepCompleted(step);
        setJustSavedSiteData(true);
        setTimeout(() => {
          setStepWithPersistence(nextStep);
          setTimeout(() => setJustSavedSiteData(false), 1000);
        }, 200);
      },
      onError: () => {
        toast.error('Algo anda mal. Por favor, contactar a soporte.');
      }
    }
  );

  // ----------CATEGORIES with localStorage integration--------------------------------------------------------------------------------

  // Initialize categories from localStorage or empty state
  const [fatherCategory, setFatherCategory] = useState<CategoryI | undefined>(
    () => localState.fatherCategory || undefined
  );
  const [childCategories, setChildCategories] = useState<SubCategoryI[]>(
    () => localState.childCategories || []
  );

  // Memoized category handlers with localStorage persistence
  const handleFatherCategory = useCallback(
    (category: CategoryI) => {
      setFatherCategory(category);
      updateLocalState({ fatherCategory: category, childCategories: [] });

      if (category.name === 'Belleza y Estética') {
        setStepWithPersistence(step + 1);
      } else {
        mutation.mutate({ category_ids: [category.id] } as any);
      }
    },
    [updateLocalState, step, setStepWithPersistence, mutation]
  );

  const handleChildCategory = useCallback(
    (subCategory: SubCategoryI) => {
      const isSubCategorySelected = childCategories.some(
        (selectedSubCategory) => selectedSubCategory.name === subCategory.name
      );

      let updatedCategories: SubCategoryI[];
      if (isSubCategorySelected) {
        updatedCategories = childCategories.filter(
          (selectedSubCategory) => selectedSubCategory.name !== subCategory.name
        );
      } else {
        updatedCategories = [...childCategories, subCategory];
      }

      setChildCategories(updatedCategories);
      updateLocalState({ childCategories: updatedCategories });
    },
    [childCategories, updateLocalState]
  );

  const onSubmitChildCategory = useCallback(() => {
    if (childCategories.length === 0) {
      markStepCompleted(step);
      setStepWithPersistence(step + 1);
    } else {
      const childCategoriesIds = childCategories.map((childCategory) => childCategory.id);
      trackGenericEvent('Button Selfonboard Category Clicked', {
        categories: childCategories
      });
      mutation.mutate({ category_ids: childCategoriesIds } as any);
    }
  }, [childCategories, step, markStepCompleted, setStepWithPersistence, mutation]);

  // Clear category state when going back to step 1
  useEffect(() => {
    if (step === 1) {
      setFatherCategory(undefined);
      updateLocalState({ fatherCategory: null, childCategories: [] });
    }
  }, [step, updateLocalState]);

  const { data: categories } = useQuery(
    ['categories'],
    async () =>
      await client<GetCategoriesI>(`categories?level=1`, 'GET', {
        isAuthRequired: true
      }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: step === 1 || step === 2
    }
  );

  // ----------LOCATIONS with localStorage integration--------------------------------------------------------------------------------

  const [country, setCountry] = useState<string | null>(
    () => localState.country || user?.vendor.country || null
  );
  const [state, setState] = useState<string | null>(() => localState.state || null);
  const [timezone, setTimeZone] = useState<string | null>(
    () => localState.timezone || user?.vendor.timezone || null
  );

  // Sync with user data when loaded
  useEffect(() => {
    if (user && !localState.country) {
      const userCountry = user?.vendor.country || null;
      const userTimezone = user?.vendor.timezone || null;

      if (userCountry) {
        setCountry(userCountry);
        updateLocalState({ country: userCountry });
      }
      if (userTimezone) {
        setTimeZone(userTimezone);
        updateLocalState({ timezone: userTimezone });
      }
    }
  }, [user, localState.country, updateLocalState]);

  const onSubmitLocation = useCallback(() => {
    if (!country) return toast.error('Debes seleccionar un país');
    if (!timezone) return toast.error('Debes seleccionar una zona horaria');

    trackGenericEvent('Button Selfonboard Location Clicked', {
      country,
      timezone
    });

    // Save to localStorage immediately
    updateLocalState({ country, state, timezone });
    markStepCompleted(step);

    mutation.mutate({ country, timezone, state } as any);

    setTimeout(() => {
      setStepWithPersistence(step + 1);
    }, 500);
  }, [country, timezone, state, step, updateLocalState, markStepCompleted, setStepWithPersistence, mutation]);

  //----------------------SITE DATA with localStorage integration --------------------------------------------------------------

  const [usernameIsAvailable, setUsernameIsAvailable] = useState<boolean>(true);
  const [privacyPolicies, setPrivacyPolicies] = useState<boolean>(() => localState.privacyPolicies ?? false);
  const [validationChecks, setValidationChecks] = React.useState({
    length: false,
    noTildes: false,
    noSpecialChars: false,
    noConsecutiveDots: false,
    noStartEndDots: false,
    notUrl: false
  });

  const togglePrivacyPolicies = useCallback(() => {
    const newValue = !privacyPolicies;
    setPrivacyPolicies(newValue);
    updateLocalState({ privacyPolicies: newValue });
  }, [privacyPolicies, updateLocalState]);

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue
  } = useForm({ resolver: yupResolver(getSchema(usernameIsAvailable)) });

  const username: string = useWatch({ name: 'username', control });

  React.useEffect(() => {
    if (username) {
      setValidationChecks({
        length: username.length >= MIN_USERNAME_LENGTH,
        noTildes: !TILDE_REGEX.test(username),
        noSpecialChars: !SPECIAL_CHARS_REGEX.test(username),
        noConsecutiveDots: !username.includes('..'),
        noStartEndDots: !username.startsWith('.') && !username.endsWith('.'),
        notUrl: !username.split('.').some((part) => ['com', 'co', 'red'].includes(part.toLowerCase()))
      });
    }
  }, [username]);

  const hasUsernameValidationError = React.useMemo(() => {
    if (!username || username.length < MIN_USERNAME_LENGTH) {
      return false; // Don't show validation errors for empty or too short usernames
    }
    return Object.values(validationChecks).some((check) => !check);
  }, [username, validationChecks]);

  const mutationCheckUsername = useMutation<EmptyObject, ErrorI, VerifyUsernameI>(
    (data) =>
      client<EmptyObject>(`vendors/verify_username`, 'POST', {
        isAuthRequired: true,
        data
      }),
    {
      onSuccess: (data) => {
        if (user?.vendor.username === username) {
          setUsernameIsAvailable(true);
        } else {
          setUsernameIsAvailable(!data.exist);
        }
      }
    }
  );

  React.useEffect(() => {
    if (username) {
      const sanitizedValue = username.replace(/[^a-zA-Z0-9_.-]/g, '');
      const normalizedValue = sanitizedValue.toLowerCase().trim();
      setValue('username', normalizedValue, { shouldValidate: true });
    }
  }, [username, setValue]);

  React.useEffect(() => {
    if (username && username.length >= MIN_USERNAME_LENGTH) mutationCheckUsername.mutate({ username });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [username]);

  React.useEffect(() => {
    if (user) {
      if (!control._formValues?.profession)
        setValue('profession', localState.profession || user?.vendor.professional_title || '');
      if (!control._formValues?.username)
        setValue('username', localState.username || user?.vendor.username || '');
      if (!control._formValues?.storefront_name)
        setValue('storefront_name', localState.storefrontName || user?.vendor.vendor_storefront?.name || '');
      if (!control._formValues?.phone) setValue('phone', localState.phone || user?.phone || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, setValue, control]);

  const onSubmitSiteData: SubmitHandler<FieldValues> = async (data) => {
    if (hasUsernameValidationError) {
      toast.error('Por favor revisa tu nombre de usuario');
      return;
    } else if (!privacyPolicies) {
      toast.error('Debes aceptar los términos y condiciones para continuar');
      return;
    } else {
      const request: Partial<PostVendorInfoI> = {
        professional_title: data.profession,
        username: data.username,
        storefront_name: data.storefront_name
      };

      // Persist form data to localStorage
      updateLocalState({
        profession: data.profession,
        username: data.username,
        storefrontName: data.storefront_name
      });

      trackGenericEvent('Button Selfonboard Site Data Clicked', {
        username: data.username,
        profession: data.profession,
        storefront_name: data.storefront_name
      });

      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      mutation.mutate(request as any);
    }
  };

  const fullSubmitSiteData = () => {
    handleSubmit(onSubmitSiteData, (e) => {
      if (e) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const errors = Object.values(e).map((error) => error?.message);
        toast.error(errors.join('\r\n'));
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        toast.error('Error al guardar los datos');
      }
    })();
  };

  // ---------------------PHONE with localStorage integration-------------------------------------------------------------------

  const [phone, setPhone] = useState<string>(() => localState.phone || '');

  const onChangePhone = useCallback(
    (phone: string) => {
      setPhone(phone);
      updateLocalState({ phone });
    },
    [updateLocalState]
  );

  React.useEffect(() => {
    setPhone(user?.phone || '');
  }, [user]);

  const onSubmitPhone = useCallback(() => {
    // Persist phone to localStorage
    updateLocalState({ phone });
    markStepCompleted(step);

    trackGenericEvent('Button Selfonboard Phone Clicked', {
      phone
    });
    updateIntercomPhone(phone);
    mutation.mutate({ phone } as any);
  }, [phone, updateLocalState, markStepCompleted, step, mutation]);

  // ---------------------------------------------------------------------------------------------------

  const [loadingState, setLoadingState] = useState<boolean>(false);

  useEffect(() => {
    const isLoading = mutation.isLoading || mutationCheckUsername.isLoading;
    setLoadingState(isLoading);
  }, [mutation.isLoading, mutationCheckUsername.isLoading]);

  if (!user) return <Loader fullHeight />;

  return (
    <OnboardingContext.Provider
      value={{
        user,
        mutation,
        refetch,
        setValue,
        step,
        setStep: setStepWithPersistence,
        loadingState,
        setLoadingState,
        //LOCATIONS-----------
        country,
        setCountry,
        state,
        setState,
        timezone,
        setTimeZone,
        onSubmitLocation,
        //CATEGORIES-----------
        categories,
        handleFatherCategory,
        fatherCategory,
        handleChildCategory,
        childCategories,
        onSubmitChildCategory,
        //SITE DATA-----------
        onSubmitSiteData,
        username,
        control,
        usernameIsAvailable,
        fullSubmitSiteData,
        privacyPolicies,
        togglePrivacyPolicies,
        errors,
        hasValidationError: hasUsernameValidationError,
        validationChecks,
        //PHONE----------------
        phone,
        onChangePhone,
        onSubmitPhone
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }

  return context;
};
